import React, { createContext, useReducer, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { User } from '@supabase/supabase-js';
import { useToast } from '@/contexts/ToastContext';

export interface Profile {
  id: string;
  email?: string;
  role?: 'admin' | 'staff';
  created_at?: string;
  avatar_url?: string;
}

interface AuthState {
  user: User | null;
  profile: Profile | null;
  isLoading: boolean;
  isInitialized: boolean;
}

interface AuthContextType extends AuthState {
  dispatch: React.Dispatch<AuthAction>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
}

const initialState: AuthState = {
  user: null,
  profile: null,
  isLoading: true,
  isInitialized: false,
};

type AuthAction =
  | { type: 'SET_USER'; user: User }
  | { type: 'SET_PROFILE'; profile: Profile }
  | { type: 'INITIALIZE' }
  | { type: 'RESET' };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_USER':
      return {
        ...state,
        user: action.user,
        // Only set loading true if we don't have a profile yet
        isLoading: !state.profile,
      };
    case 'SET_PROFILE':
      return {
        ...state,
        profile: action.profile,
        isLoading: false,
      };
    case 'INITIALIZE':
      return {
        ...state,
        isInitialized: true,
        isLoading: false,
      };
    case 'RESET':
      return {
        ...initialState,
        isInitialized: true,
        isLoading: false,
      };
    default:
      return state;
  }
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { addToast } = useToast();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const initialSessionChecked = useRef(false);
  const currentUserRef = useRef<User | null>(null);

  const fetchAndSetProfile = async (userId: string) => {
    try {
      // Don't fetch profile if we already have it for this user
      if (state.profile?.id === userId) {
        return;
      }

      const { data: profile, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      dispatch({ type: 'SET_PROFILE', profile });
    } catch (error) {
      console.error('Error fetching user profile:', error);
      addToast({
        type: 'error',
        message: 'Failed to load user profile'
      });
      dispatch({ type: 'RESET' });
    }
  };

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // If we already have a user and profile, don't re-initialize
        if (state.user && state.profile && !state.isLoading) {
          return;
        }

        // Set a timeout to prevent infinite loading
        timeoutRef.current = setTimeout(() => {
          if (!initialSessionChecked.current && mounted) {
            console.error('Session check timed out');
            dispatch({ type: 'RESET' });
            addToast({
              type: 'error',
              message: 'Session check timed out. Please try refreshing the page.'
            });
          }
        }, 10000); // 10 second timeout

        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) throw error;

        if (mounted) {
          if (session?.user) {
            // Only update if the user has changed
            if (currentUserRef.current?.id !== session.user.id) {
              currentUserRef.current = session.user;
              dispatch({ type: 'SET_USER', user: session.user });
              await fetchAndSetProfile(session.user.id);
            }
          } else {
            dispatch({ type: 'RESET' });
          }

          initialSessionChecked.current = true;
          dispatch({ type: 'INITIALIZE' });
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) {
          dispatch({ type: 'RESET' });
          addToast({
            type: 'error',
            message: 'Failed to initialize authentication'
          });
        }
      } finally {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      }
    };

    void initializeAuth();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted || !initialSessionChecked.current) {
        console.log('Skipping auth state change, component unmounted or waiting for initial session');
        return;
      }

      console.log('Auth state changed:', event);

      if (event === 'SIGNED_OUT' || !session) {
        currentUserRef.current = null;
        dispatch({ type: 'RESET' });
        return;
      }

      if (session.user) {
        // Only update if the user has changed
        if (currentUserRef.current?.id !== session.user.id) {
          currentUserRef.current = session.user;
          dispatch({ type: 'SET_USER', user: session.user });
          await fetchAndSetProfile(session.user.id);
        }
      }
    });

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && state.user && state.profile) {
        // If we already have a user and profile, just verify the session is still valid
        void supabase.auth.getSession().then(({ data: { session } }) => {
          if (!session) {
            dispatch({ type: 'RESET' });
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      subscription.unsubscribe();
    };
  }, [addToast, state.user, state.profile, state.isLoading]);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      if (data.user) {
        dispatch({ type: 'SET_USER', user: data.user });
        await fetchAndSetProfile(data.user.id);
      }
    } catch (error) {
      console.error('Error signing in:', error);
      addToast({
        type: 'error',
        message: 'Failed to sign in'
      });
      dispatch({ type: 'RESET' });
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      dispatch({ type: 'RESET' });
    } catch (error) {
      console.error('Error signing out:', error);
      addToast({
        type: 'error',
        message: 'Failed to sign out'
      });
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password
      });

      if (error) throw error;

      if (data.user) {
        dispatch({ type: 'SET_USER', user: data.user });
        await fetchAndSetProfile(data.user.id);
      }
    } catch (error) {
      console.error('Error signing up:', error);
      addToast({
        type: 'error',
        message: 'Failed to sign up'
      });
      dispatch({ type: 'RESET' });
    }
  };

  const updatePassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password
      });

      if (error) throw error;

      addToast({
        type: 'success',
        message: 'Password updated successfully'
      });
    } catch (error) {
      console.error('Error updating password:', error);
      addToast({
        type: 'error',
        message: 'Failed to update password'
      });
    }
  };

  return (
    <AuthContext.Provider value={{
      ...state,
      dispatch,
      signIn,
      signOut,
      signUp,
      updatePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
}